(() => {
  'use strict';

  const ELEMENT_NAME = 'okw-q03-v2-shipping-estimator';
  const CART_JSON_URL = '/cart.js';
  const STORAGE_KEY = 'okw_shipping_destination_v1';
  const STORAGE_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;
  const MANIFEST_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const CART_CHANGE_DELAY_MS = 120;
  const PROVINCE_CODE_RE = /^JP-(0[1-9]|[1-3][0-9]|4[0-7])$/;
  const NO_DESTINATION = 'お届け先を選ぶと送料と合計の目安がわかります。';
  const PDP_UNAVAILABLE = 'この商品の送料は商品ページでは算出できません。カートでご確認ください。';
  const PDP_CART_PENDING = 'カートの内容を読み込み、概算を計算しています。';
  const PDP_CART_UNAVAILABLE = 'カートの内容を確認できないため、送料・合計を算出できません。カート・決済画面でご確認ください。';
  const PDP_CART_PROFILE_UNAVAILABLE = '異なる配送条件の商品、または配送条件を確認できない商品がカートに含まれるため、この画面では送料・合計を算出できません。カート・決済画面でご確認ください。';
  const DESTINATION_UNAVAILABLE = 'この商品は選択した地域へお届けできません。';
  const FINAL_AMOUNT_NOTE = '最終額は決済画面で確定します。';
  const CART_PENDING = '実際の商品構成で送料を再計算しています。';
  const CART_UNAVAILABLE = '送料を確認できませんでした。決済画面でご確認ください。';

  let manifestPromise = null;


  function resolveVariantId({ variantJsonText = '', formVariantId = '', checkedVariantId = '' } = {}) {
    if (variantJsonText) {
      try {
        const jsonId = JSON.parse(variantJsonText)?.id;
        if (jsonId !== null && jsonId !== undefined && String(jsonId)) return String(jsonId);
      } catch (_error) {
        // Fall through to the other live DOM sources.
      }
    }
    if (String(formVariantId || '')) return String(formVariantId);
    if (String(checkedVariantId || '')) return String(checkedVariantId);
    return '';
  }

  function validProvinceCode(value) {
    return typeof value === 'string' && PROVINCE_CODE_RE.test(value);
  }

  function parseStoredDestination(raw, nowMs = Date.now()) {
    if (!raw) return null;
    let value;
    try {
      value = JSON.parse(raw);
    } catch (_error) {
      return null;
    }
    if (
      value?.schema !== 1 ||
      value?.countryCode !== 'JP' ||
      !validProvinceCode(value?.provinceCode) ||
      typeof value?.selectedAt !== 'string'
    ) return null;
    const selectedAtMs = Date.parse(value.selectedAt);
    if (!Number.isFinite(selectedAtMs) || selectedAtMs > nowMs || nowMs - selectedAtMs > STORAGE_MAX_AGE_MS) return null;
    return value;
  }

  function manifestIsUsable(manifest, nowMs = Date.now()) {
    if (
      manifest?.schema !== 'okw-shipping-rules-v1' ||
      typeof manifest?.sourceHash !== 'string' ||
      !manifest?.variantProfile ||
      !manifest?.profiles ||
      manifest?.capabilities?.rateProviders?.length !== 1 ||
      manifest.capabilities.rateProviders[0] !== 'DeliveryRateDefinition' ||
      manifest.capabilities.methodConditions !== false ||
      manifest.capabilities.oneOriginLocationPerProfile !== true
    ) return false;
    const generatedAtMs = Date.parse(manifest.generatedAt);
    return Number.isFinite(generatedAtMs) && generatedAtMs <= nowMs && nowMs - generatedAtMs <= MANIFEST_MAX_AGE_MS;
  }

  function estimatePdp({ manifest, variantId, priceJpy, quantity, provinceCode, requiresShipping = true, nowMs = Date.now() }) {
    if (!validProvinceCode(provinceCode)) return { status: 'no_destination', reason: 'destination_missing' };
    if (!manifestIsUsable(manifest, nowMs)) return { status: 'unavailable', reason: 'ruleset_stale' };
    if (!requiresShipping) return { status: 'unavailable', reason: 'requires_shipping_unsupported' };
    const normalizedVariantId = String(variantId || '');
    const profileKey = manifest.variantProfile[normalizedVariantId];
    const profile = profileKey ? manifest.profiles[profileKey] : null;
    if (!profile) return { status: 'unavailable', reason: 'profile_unknown' };
    if (!Object.prototype.hasOwnProperty.call(profile.provinceRatesJpy || {}, provinceCode)) {
      return { status: 'destination_unavailable', reason: 'destination_unavailable', profileKey };
    }
    const shippingJpy = profile.provinceRatesJpy[provinceCode];
    const safeQuantity = Math.max(1, Number.parseInt(quantity, 10) || 1);
    if (!Number.isInteger(priceJpy) || priceJpy < 0 || !Number.isInteger(shippingJpy) || shippingJpy < 0) {
      return { status: 'unavailable', reason: 'rate_invalid', profileKey };
    }
    const productSubtotalJpy = priceJpy * safeQuantity;
    return {
      status: 'ok',
      profileKey,
      quantity: safeQuantity,
      productSubtotalJpy,
      shippingJpy,
      estimatedTotalJpy: productSubtotalJpy + shippingJpy
    };
  }

  function normalizeLiveCart(payload) {
    if (!payload || !Array.isArray(payload.items)) return { status: 'unavailable', reason: 'cart_get_failed' };
    if (!Number.isInteger(payload.total_price) || payload.total_price < 0 || payload.total_price % 100 !== 0) {
      return { status: 'unavailable', reason: 'cart_total_invalid' };
    }
    const shippableVariantIds = [];
    for (const item of payload.items) {
      if (typeof item?.requires_shipping !== 'boolean') {
        return { status: 'unavailable', reason: 'cart_shipping_requirement_unknown' };
      }
      if (!item.requires_shipping) continue;
      if ((!Number.isInteger(item.variant_id) && typeof item.variant_id !== 'string') || !String(item.variant_id)) {
        return { status: 'unavailable', reason: 'cart_variant_unknown' };
      }
      shippableVariantIds.push(String(item.variant_id));
    }
    return {
      status: 'ok',
      empty: payload.items.length === 0,
      cartSubtotalJpy: payload.total_price / 100,
      shippableVariantIds
    };
  }

  function estimatePdpWithCart({ manifest, cart, variantId, priceJpy, quantity, provinceCode, requiresShipping = true, nowMs = Date.now() }) {
    const candidate = estimatePdp({ manifest, variantId, priceJpy, quantity, provinceCode, requiresShipping, nowMs });
    if (candidate.status !== 'ok') return candidate;
    const normalizedCart = normalizeLiveCart(cart);
    if (normalizedCart.status !== 'ok') return normalizedCart;
    if (normalizedCart.empty) return { ...candidate, mode: 'empty_cart' };

    for (const cartVariantId of normalizedCart.shippableVariantIds) {
      const cartProfileKey = manifest.variantProfile[cartVariantId];
      if (!cartProfileKey || !manifest.profiles[cartProfileKey]) {
        return { status: 'unavailable', reason: 'cart_profile_unknown' };
      }
      if (cartProfileKey !== candidate.profileKey) {
        return { status: 'unavailable', reason: 'mixed_profiles' };
      }
    }

    const hypotheticalSubtotalJpy = normalizedCart.cartSubtotalJpy + candidate.productSubtotalJpy;
    return {
      status: 'ok',
      mode: 'hypothetical_cart',
      profileKey: candidate.profileKey,
      quantity: candidate.quantity,
      cartSubtotalJpy: normalizedCart.cartSubtotalJpy,
      candidateSubtotalJpy: candidate.productSubtotalJpy,
      productSubtotalJpy: hypotheticalSubtotalJpy,
      shippingJpy: candidate.shippingJpy,
      estimatedTotalJpy: hypotheticalSubtotalJpy + candidate.shippingJpy,
      shippableCartLineCount: normalizedCart.shippableVariantIds.length
    };
  }

  function estimateCartFromManifest({ manifest, cart, provinceCode, nowMs = Date.now() }) {
    if (!validProvinceCode(provinceCode)) return { status: 'no_destination', reason: 'destination_missing' };
    if (!manifestIsUsable(manifest, nowMs)) return { status: 'unavailable', reason: 'ruleset_stale' };
    const normalizedCart = normalizeLiveCart(cart);
    if (normalizedCart.status !== 'ok') return normalizedCart;
    if (normalizedCart.empty) return { status: 'empty', reason: 'cart_empty', cartSubtotalJpy: 0 };
    if (normalizedCart.shippableVariantIds.length === 0) {
      return { status: 'unavailable', reason: 'cart_profile_unknown' };
    }

    let profileKey = null;
    for (const cartVariantId of normalizedCart.shippableVariantIds) {
      const cartProfileKey = manifest.variantProfile[cartVariantId];
      if (!cartProfileKey || !manifest.profiles[cartProfileKey]) {
        return { status: 'unavailable', reason: 'cart_profile_unknown' };
      }
      if (profileKey && cartProfileKey !== profileKey) {
        return { status: 'unavailable', reason: 'mixed_profiles' };
      }
      profileKey = cartProfileKey;
    }

    const profile = manifest.profiles[profileKey];
    if (!Object.prototype.hasOwnProperty.call(profile.provinceRatesJpy || {}, provinceCode)) {
      return { status: 'destination_unavailable', reason: 'destination_unavailable', profileKey };
    }
    const shippingJpy = profile.provinceRatesJpy[provinceCode];
    if (!Number.isInteger(normalizedCart.cartSubtotalJpy) || normalizedCart.cartSubtotalJpy < 0 || !Number.isInteger(shippingJpy) || shippingJpy < 0) {
      return { status: 'unavailable', reason: 'rate_invalid', profileKey };
    }
    return {
      status: 'ok',
      profileKey,
      cartSubtotalJpy: normalizedCart.cartSubtotalJpy,
      shippingJpy,
      estimatedTotalJpy: normalizedCart.cartSubtotalJpy + shippingJpy,
      shippableCartLineCount: normalizedCart.shippableVariantIds.length
    };
  }

  function normalizeCartRates(payload) {
    if (!Array.isArray(payload?.shipping_rates) || payload.shipping_rates.length === 0) {
      return { status: 'unavailable', reason: 'cart_rate_empty' };
    }
    const rates = payload.shipping_rates
      .map((rate) => {
        const parsed = Number.parseFloat(rate.price);
        return {
          name: String(rate.name || rate.presentment_name || '配送'),
          priceJpy: Number.isFinite(parsed) ? Math.round(parsed) : NaN
        };
      })
      .filter((rate) => Number.isInteger(rate.priceJpy) && rate.priceJpy >= 0);
    if (rates.length === 0) return { status: 'unavailable', reason: 'cart_rate_invalid' };
    const distinctAmounts = [...new Set(rates.map((rate) => rate.priceJpy))];
    if (distinctAmounts.length > 1) return { status: 'multiple', reason: 'multiple_rates_ambiguous', rates };
    return { status: 'ok', priceJpy: distinctAmounts[0], rates };
  }

  function formatJpy(value) {
    return `¥${new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(value)}`;
  }

  function pdpPresentation(estimate) {
    return {
      summaryTotalLabel: '合計（概算）'
    };
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function readStorage(storage = window.localStorage, nowMs = Date.now()) {
    try {
      const raw = storage.getItem(STORAGE_KEY);
      const parsed = parseStoredDestination(raw, nowMs);
      if (raw && !parsed) storage.removeItem(STORAGE_KEY);
      return parsed;
    } catch (_error) {
      return null;
    }
  }

  function writeStorage(provinceCode, surface, storage = window.localStorage, now = new Date()) {
    if (!validProvinceCode(provinceCode)) return false;
    const value = {
      schema: 1,
      countryCode: 'JP',
      provinceCode,
      selectedAt: now.toISOString(),
      source: surface === 'cart' ? 'cart' : 'pdp'
    };
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(value));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function fetchManifest(url) {
    if (!manifestPromise) {
      manifestPromise = fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error('manifest_http_error');
          return response.json();
        });
    }
    return manifestPromise;
  }

  async function fetchLiveCart(signal) {
    const response = await fetch(`${CART_JSON_URL}?okw_estimator=${Date.now()}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal
    });
    if (!response.ok) throw new Error('cart_json_http_error');
    return response.json();
  }

  class OkawariShippingEstimator extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._requestToken = 0;
      this._controller = null;
      this._select = this.querySelector('[data-estimator-province]');
      this._result = this.querySelector('[data-estimator-result]');
      this._surface = this.dataset.surface === 'cart' ? 'cart' : 'pdp';
      if (!this._select || !this._result) return;

      this._onDestination = () => {
        const provinceCode = this._select.value;
        if (validProvinceCode(provinceCode)) writeStorage(provinceCode, this._surface);
        this.recalculate('destination');
      };
      this._select.addEventListener('change', this._onDestination);

      if (this._surface === 'pdp') {
        this.bindPdpInputs();
        this.bindPdpCartChanges();
      }
      const restored = readStorage();
      if (restored) this._select.value = restored.provinceCode;
      this.recalculate(restored ? 'initial_restore' : 'initial');
    }

    disconnectedCallback() {
      this._connected = false;
      this._select?.removeEventListener('change', this._onDestination);
      this._quantityInputs?.forEach((input) => {
        input.removeEventListener('input', this._onQuantity);
        input.removeEventListener('change', this._onQuantity);
      });
      this._quantityControls?.forEach((control) => control.removeEventListener('click', this._onQuantityButton));
      this._variantPicker?.removeEventListener('VARIANT_CHANGE', this._onVariant);
      document.removeEventListener('cart-updated', this._onCartUpdated, true);
      this._cartMutationObserver?.disconnect();
      this._cartMutationObserver = null;
      if (this._cartChangeTimer !== null && this._cartChangeTimer !== undefined) clearTimeout(this._cartChangeTimer);
      this._cartChangeTimer = null;
      this._controller?.abort();
    }

    bindPdpInputs() {
      this._scope = this.closest('.product-text') || this.closest('[data-product-id]') || this.parentElement;
      this._quantityInputs = [...(this._scope?.querySelectorAll('input[name="quantity"], [data-js-product-quantity] input') || [])];
      this._onQuantity = () => this.recalculate('quantity');
      this._quantityInputs.forEach((input) => {
        input.addEventListener('input', this._onQuantity);
        input.addEventListener('change', this._onQuantity);
      });
      this._quantityControls = [...new Set(this._quantityInputs.flatMap((input) => [
        ...(input.closest('product-quantity')?.querySelectorAll('.qty-minus, .qty-plus') || [])
      ]))];
      this._quantityButtonQueued = false;
      this._onQuantityButton = () => {
        if (this._quantityButtonQueued) return;
        this._quantityButtonQueued = true;
        queueMicrotask(() => {
          this._quantityButtonQueued = false;
          if (this._connected) this.recalculate('quantity_button');
        });
      };
      this._quantityControls.forEach((control) => control.addEventListener('click', this._onQuantityButton));
      this._variantPicker = this._scope?.querySelector('product-variants') || null;
      this._onVariant = () => queueMicrotask(() => this.recalculate('variant'));
      this._variantPicker?.addEventListener('VARIANT_CHANGE', this._onVariant);
      try {
        const variantsJson = this.querySelector('[data-estimator-variants]')?.textContent || '[]';
        this._variants = JSON.parse(variantsJson);
      } catch (_error) {
        this._variants = [];
      }
    }

    bindPdpCartChanges() {
      if (this._surface !== 'pdp') return;
      this._cartChangeTimer = null;
      this._onCartUpdated = () => this.queueCartRecalculation('cart_updated');
      document.addEventListener('cart-updated', this._onCartUpdated, true);
      const headerCartTargets = [...new Set(document.querySelectorAll('[data-header-cart-count], [data-header-cart-total]'))];
      if (headerCartTargets.length > 0) {
        this._cartMutationObserver = new MutationObserver(() => this.queueCartRecalculation('header_cart_mutation'));
        headerCartTargets.forEach((target) => this._cartMutationObserver.observe(target, {
          childList: true,
          characterData: true,
          subtree: true
        }));
      }
    }

    queueCartRecalculation(trigger) {
      if (!this._connected || this._surface !== 'pdp') return;
      if (this._cartChangeTimer !== null && this._cartChangeTimer !== undefined) clearTimeout(this._cartChangeTimer);
      this._cartChangeTimer = setTimeout(() => {
        this._cartChangeTimer = null;
        if (this._connected && this._surface === 'pdp') this.recalculate(`cart_change:${trigger}`);
      }, CART_CHANGE_DELAY_MS);
    }

    currentVariant() {
      const variantJsonText = this._variantPicker?.querySelector('[data-js-variant-data]')?.textContent || '';
      const formVariantId = this._scope?.querySelector('product-form input[name="id"]')?.value || '';
      const checkedVariantId = this._variantPicker?.querySelector('[data-variant-id]:checked')?.dataset.variantId || '';
      const variantId = resolveVariantId({ variantJsonText, formVariantId, checkedVariantId });
      return this._variants.find((variant) => String(variant.id) === variantId) || null;
    }

    currentQuantity() {
      const input = this._quantityInputs?.find((candidate) => !candidate.disabled);
      return Math.max(1, Number.parseInt(input?.value || '1', 10) || 1);
    }

    setMessage(text, state = 'idle') {
      this.dataset.state = state;
      const message = `<p class="okw-q03-v2-estimator__message" data-estimator-message>${escapeHtml(text)}</p>`;
      this._result.innerHTML = message;
    }

    renderRows({ context, subtotalJpy, shippingJpy, totalJpy, note }) {
      this.dataset.state = 'ready';
      this._result.innerHTML = `
        <p class="okw-q03-v2-estimator__context">${escapeHtml(context)}</p>
        <dl class="okw-q03-v2-estimator__rows">
          <div class="okw-q03-v2-estimator__row"><dt>商品小計</dt><dd class="okw-q03-v2-estimator__amount">${formatJpy(subtotalJpy)}</dd></div>
          <div class="okw-q03-v2-estimator__row"><dt>送料</dt><dd class="okw-q03-v2-estimator__amount">${formatJpy(shippingJpy)}</dd></div>
          <div class="okw-q03-v2-estimator__row okw-q03-v2-estimator__row--total"><dt>合計（概算）</dt><dd class="okw-q03-v2-estimator__amount">${formatJpy(totalJpy)}</dd></div>
        </dl>
        <p class="okw-q03-v2-estimator__note">${escapeHtml(note)}</p>`;
    }

    renderPdpEstimate(estimate) {
      const presentation = pdpPresentation(estimate);
      this.dataset.state = 'ready';
      this._result.innerHTML = `
        <div class="okw-q03-v2-estimator__summary-block">
          <dl class="okw-q03-v2-estimator__summary" aria-label="送料と合計の概算">
            <div class="okw-q03-v2-estimator__metric"><dt>送料</dt><dd class="okw-q03-v2-estimator__amount">${formatJpy(estimate.shippingJpy)}</dd></div>
            <div class="okw-q03-v2-estimator__metric okw-q03-v2-estimator__metric--total"><dt>${escapeHtml(presentation.summaryTotalLabel)}</dt><dd class="okw-q03-v2-estimator__amount">${formatJpy(estimate.estimatedTotalJpy)}</dd></div>
          </dl>
          <p class="okw-q03-v2-estimator__final-note">${escapeHtml(FINAL_AMOUNT_NOTE)}</p>
        </div>`;
    }

    async recalculate(trigger) {
      const token = ++this._requestToken;
      const provinceCode = this._select?.value || '';
      if (!validProvinceCode(provinceCode)) {
        this.setMessage(NO_DESTINATION);
        return;
      }
      if (this._surface === 'cart') {
        await this.recalculateCart(provinceCode, token, trigger);
        return;
      }
      await this.recalculatePdp(provinceCode, token);
    }

    async recalculatePdp(provinceCode, token) {
      let manifest;
      try {
        manifest = await fetchManifest(this.dataset.rulesUrl);
      } catch (_error) {
        if (token === this._requestToken) this.setMessage(PDP_UNAVAILABLE, 'error');
        return;
      }
      if (token !== this._requestToken) return;
      this._controller?.abort();
      this._controller = new AbortController();
      this.setMessage(PDP_CART_PENDING, 'pending');
      let cart;
      try {
        cart = await fetchLiveCart(this._controller.signal);
      } catch (error) {
        if (error?.name !== 'AbortError' && token === this._requestToken) this.setMessage(PDP_CART_UNAVAILABLE, 'error');
        return;
      }
      if (token !== this._requestToken) return;
      const variant = this.currentVariant();
      const estimate = estimatePdpWithCart({
        manifest,
        cart,
        variantId: variant?.id,
        priceJpy: variant?.priceJpy,
        quantity: this.currentQuantity(),
        provinceCode,
        requiresShipping: variant?.requiresShipping
      });
      if (estimate.status === 'destination_unavailable') {
        this.setMessage(DESTINATION_UNAVAILABLE, 'error');
      } else if (estimate.reason === 'mixed_profiles' || estimate.reason === 'cart_profile_unknown' || estimate.reason === 'cart_shipping_requirement_unknown' || estimate.reason === 'cart_variant_unknown') {
        this.setMessage(PDP_CART_PROFILE_UNAVAILABLE, 'error');
      } else if (estimate.reason === 'cart_get_failed' || estimate.reason === 'cart_total_invalid') {
        this.setMessage(PDP_CART_UNAVAILABLE, 'error');
      } else if (estimate.status !== 'ok') {
        this.setMessage(PDP_UNAVAILABLE, 'error');
      } else {
        this.renderPdpEstimate(estimate);
      }
    }

    async recalculateCart(provinceCode, token) {
      let manifest;
      try {
        manifest = await fetchManifest(this.dataset.rulesUrl);
      } catch (_error) {
        if (token === this._requestToken) this.setMessage(CART_UNAVAILABLE, 'error');
        return;
      }
      if (token !== this._requestToken) return;

      this._controller?.abort();
      this._controller = new AbortController();
      this.setMessage(CART_PENDING, 'pending');
      try {
        const cart = await fetchLiveCart(this._controller.signal);
        if (token !== this._requestToken) return;
        const estimate = estimateCartFromManifest({ manifest, cart, provinceCode });
        if (estimate.status === 'empty') {
          this.setMessage('カートに商品を入れると送料の概算を表示します。');
          return;
        }
        if (estimate.status !== 'ok') {
          this.setMessage(CART_UNAVAILABLE, 'error');
          return;
        }
        this.renderRows({
          context: 'カート内の商品構成での概算',
          subtotalJpy: estimate.cartSubtotalJpy,
          shippingJpy: estimate.shippingJpy,
          totalJpy: estimate.estimatedTotalJpy,
          note: FINAL_AMOUNT_NOTE
        });
      } catch (error) {
        if (error?.name !== 'AbortError' && token === this._requestToken) this.setMessage(CART_UNAVAILABLE, 'error');
      }
    }
  }

  window.OKWQ03v2TestHooks = Object.freeze({
    validProvinceCode,
    resolveVariantId,
    parseStoredDestination,
    manifestIsUsable,
    estimatePdp,
    normalizeLiveCart,
    estimatePdpWithCart,
    estimateCartFromManifest,
    pdpPresentation,
    normalizeCartRates,
    formatJpy,
    constants: Object.freeze({ STORAGE_KEY, STORAGE_MAX_AGE_MS, MANIFEST_MAX_AGE_MS, CART_CHANGE_DELAY_MS })
  });

  if (!customElements.get(ELEMENT_NAME)) customElements.define(ELEMENT_NAME, OkawariShippingEstimator);
})();
